import { TopicGraphNodeData, validateTopicGraph as validateTopicGraphCore } from '@arnold/core';
import { DataNode, NodeData, OutputKey } from './dataNode';
import { COMMENT_TYPE } from './constants';
import { QuestionType } from '../../generated/hooks';

export type ValidationError = {
  node: DataNode;
  part:
    | 'questionText'
    | 'input'
    | 'reportOrder'
    | 'min'
    | 'max'
    | 'min/max'
    | `choice-opt-${number}`
    | `output-opt-${number}`
    | `statement-${number}`;
  error:
    | 'validationNoConnection'
    | 'validationEmpty'
    | 'validationMinLesserThan1'
    | 'validationMinLesserThan0'
    | 'validationMaxLesserThan2'
    | 'validationMaxLesserThan1'
    | 'validationMaxLesserThanMin'
    | 'validationMaxGreaterThanOptions'
    | 'validationReportOrderUnique'
    | 'validationMissingStartNode'
    | 'validationMultipleStartNodes'
    | 'validationMissingEndingNode'
    | 'validationMultipleEndingNodes'
    | 'validationEndingMustHaveInput'
    | 'validationEndingMustHaveNoOutputs'
    | 'validationUnreachableFromStart';
};

const toTopicGraphNodeData = (data: NodeData): TopicGraphNodeData => ({
  questionIndex: data.questionIndex,
  questionText: data.questionText,
  isRequired: data.isRequired,
  metrics: data.metrics,
  isQuestionTrigger: data.isQuestionTrigger,
  choices: data.choices,
  statements: data.statements,
  type: data.type,
  range: data.range,
  input: data.input,
  outputs: data.outputs,
  position: data.position,
  reportOrder: data.reportOrder,
  commentGroupId: data.commentGroupId,
});
export const validateTranslationTexts = (nodes: DataNode[]): ValidationError[] => {
  const errors: ValidationError[] = [];

  nodes.forEach((node) => {
    if (node.data.type === COMMENT_TYPE) return;

    if (node.data.questionText.trim().length === 0) {
      errors.push({ error: 'validationEmpty', part: 'questionText', node });
    }

    Object.entries(node.data.choices).forEach((choiceEntry) => {
      const [choiceKey, choiceText] = choiceEntry as [OutputKey, string];
      if (choiceText.trim().length === 0) {
        errors.push({ error: 'validationEmpty', part: `choice-${choiceKey}`, node });
      }
    });

    if (node.data.type === QuestionType.Scale) {
      (node.data.statements ?? []).forEach((statementText, index) => {
        if (statementText != null && statementText.trim().length === 0) {
          errors.push({ error: 'validationEmpty', part: `statement-${index}`, node });
        }
      });
    }
  });

  return errors;
};

/**
 * Used for validation before save
 * @param nodes nodes to be validated
 * @param allNodes all nodes in the editor (used for checking connections to the node being validated)
 * @returns array of validationError
 */
export const validateNodes = (nodes: DataNode[], allNodes: DataNode[]): ValidationError[] => {
  nodes.forEach((node) => {
    node.errors = [];
  });

  const graphNodes = nodes.map((node) => toTopicGraphNodeData(node.data));
  const allGraphNodes = allNodes.map((node) => toTopicGraphNodeData(node.data));
  const coreErrors = validateTopicGraphCore(graphNodes, allGraphNodes);

  coreErrors.forEach((coreError) => {
    const node = nodes.find((n) => n.data.questionIndex === coreError.questionIndex)!;
    node.errors.push({
      node,
      part: coreError.part as ValidationError['part'],
      error: coreError.error,
    });
  });

  return nodes.flatMap((node) => node.errors);
};
